/**
 * Demo Targeting Data
 * Pre-filled targeting data for demo accounts on the settings page.
 * Each demo account has similar Instagram accounts and relevant hashtags.
 */

import type { SpeedOption, GenderOption } from "@/components/growth-settings/types"

export interface DemoTarget {
    id: string
    name: string
    type: "user" | "hashtag"
    thumbnail: string
    count: number
    platform: "instagram"
    source: "ai_onboarding"
}

export interface DemoTargetingOptions {
    languages: string[]
    locations: string[]
    interests: string[]
    general: {
        speed: SpeedOption
        gender: GenderOption
    }
}

// Demo Account IDs
const RHODE_ID = "654c6b38-2235-402b-85f9-823e3da306e9"
const EMMA_CHAMBERLAIN_ID = "c7a8face-cc36-4505-9cb1-78b6a49354a6"
const MATILDA_DJERF_ID = "06986620-54f0-444d-9fff-b92495c92680"

// Helper to generate hashtag placeholder thumbnails (Instagram pink background)
const hashtagThumbnail = (tag: string) =>
    `https://ui-avatars.com/api/?name=%23${tag}&background=E1306C&color=fff&rounded=true&size=150`

// Helper to get Instagram profile pictures from Supabase storage
// Demo avatars are pre-downloaded and stored in the avatars bucket
// Pattern: https://db.outfame.com/storage/v1/object/public/avatars/demo-{username}.jpg
const userThumbnail = (username: string) =>
    `https://db.outfame.com/storage/v1/object/public/avatars/demo-${username}.jpg`

/**
 * @rhode - Beauty/Skincare (Hailey Bieber's brand)
 * Target audience: Women interested in skincare, clean beauty, celebrity brands
 */
const rhodeTargets: DemoTarget[] = [
    // Similar User Accounts - Beauty/Skincare Influencers
    {
        id: "demo-rhode-user-1",
        name: "hyram",
        type: "user",
        thumbnail: userThumbnail("hyram"),
        count: 4_500_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-user-2",
        name: "marianna_hewitt",
        type: "user",
        thumbnail: userThumbnail("marianna_hewitt"),
        count: 1_100_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-user-3",
        name: "patrickta",
        type: "user",
        thumbnail: userThumbnail("patrickta"),
        count: 3_200_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-user-4",
        name: "nikkietutorials",
        type: "user",
        thumbnail: userThumbnail("nikkietutorials"),
        count: 18_900_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-user-5",
        name: "jamescharles",
        type: "user",
        thumbnail: userThumbnail("jamescharles"),
        count: 22_000_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-user-6",
        name: "skinbydrazi",
        type: "user",
        thumbnail: userThumbnail("skinbydrazi"),
        count: 1_800_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-user-7",
        name: "jackieaina",
        type: "user",
        thumbnail: userThumbnail("jackieaina"),
        count: 3_500_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    // Hashtags - Skincare/Beauty
    {
        id: "demo-rhode-hash-1",
        name: "skincare",
        type: "hashtag",
        thumbnail: hashtagThumbnail("skincare"),
        count: 98_000_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-hash-2",
        name: "glowingskin",
        type: "hashtag",
        thumbnail: hashtagThumbnail("glowingskin"),
        count: 24_500_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-hash-3",
        name: "skincareroutine",
        type: "hashtag",
        thumbnail: hashtagThumbnail("skincareroutine"),
        count: 31_200_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-hash-4",
        name: "cleanbeauty",
        type: "hashtag",
        thumbnail: hashtagThumbnail("cleanbeauty"),
        count: 18_700_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-hash-5",
        name: "skincareproducts",
        type: "hashtag",
        thumbnail: hashtagThumbnail("skincareproducts"),
        count: 12_400_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-hash-6",
        name: "beautybrand",
        type: "hashtag",
        thumbnail: hashtagThumbnail("beautybrand"),
        count: 4_200_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-hash-7",
        name: "lipgloss",
        type: "hashtag",
        thumbnail: hashtagThumbnail("lipgloss"),
        count: 8_900_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-hash-8",
        name: "hydration",
        type: "hashtag",
        thumbnail: hashtagThumbnail("hydration"),
        count: 6_100_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-hash-9",
        name: "glassskin",
        type: "hashtag",
        thumbnail: hashtagThumbnail("glassskin"),
        count: 3_800_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-rhode-hash-10",
        name: "skincareaddict",
        type: "hashtag",
        thumbnail: hashtagThumbnail("skincareaddict"),
        count: 9_200_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
]

/**
 * @emmachamberlain - Lifestyle/Coffee/Gen-Z Creator
 * Target audience: Gen-Z lifestyle enthusiasts, coffee lovers, content creators
 */
const emmaChamberlainTargets: DemoTarget[] = [
    // Similar User Accounts - Gen-Z Lifestyle Influencers
    {
        id: "demo-emma-user-1",
        name: "daviddobrik",
        type: "user",
        thumbnail: userThumbnail("daviddobrik"),
        count: 12_500_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-user-2",
        name: "alix_earle",
        type: "user",
        thumbnail: userThumbnail("alix_earle"),
        count: 5_400_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-user-3",
        name: "avani",
        type: "user",
        thumbnail: userThumbnail("avani"),
        count: 18_000_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-user-4",
        name: "chfrench",
        type: "user",
        thumbnail: userThumbnail("chfrench"),
        count: 890_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-user-5",
        name: "rickeythompson",
        type: "user",
        thumbnail: userThumbnail("rickeythompson"),
        count: 6_200_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-user-6",
        name: "addisonraee",
        type: "user",
        thumbnail: userThumbnail("addisonraee"),
        count: 39_000_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-user-7",
        name: "tatemcrae",
        type: "user",
        thumbnail: userThumbnail("tatemcrae"),
        count: 8_400_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-user-8",
        name: "willsmith",
        type: "user",
        thumbnail: userThumbnail("willsmith"),
        count: 28_000_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    // Hashtags - Lifestyle/Coffee/Content Creation
    {
        id: "demo-emma-hash-1",
        name: "coffeeaddict",
        type: "hashtag",
        thumbnail: hashtagThumbnail("coffeeaddict"),
        count: 14_200_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-hash-2",
        name: "lifestyle",
        type: "hashtag",
        thumbnail: hashtagThumbnail("lifestyle"),
        count: 87_000_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-hash-3",
        name: "contentcreator",
        type: "hashtag",
        thumbnail: hashtagThumbnail("contentcreator"),
        count: 42_500_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-hash-4",
        name: "aesthetic",
        type: "hashtag",
        thumbnail: hashtagThumbnail("aesthetic"),
        count: 65_000_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-hash-5",
        name: "coffeelover",
        type: "hashtag",
        thumbnail: hashtagThumbnail("coffeelover"),
        count: 21_800_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-hash-6",
        name: "genzfashion",
        type: "hashtag",
        thumbnail: hashtagThumbnail("genzfashion"),
        count: 2_400_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-hash-7",
        name: "vlogger",
        type: "hashtag",
        thumbnail: hashtagThumbnail("vlogger"),
        count: 9_800_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-hash-8",
        name: "aestheticvibes",
        type: "hashtag",
        thumbnail: hashtagThumbnail("aestheticvibes"),
        count: 5_600_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-hash-9",
        name: "morningcoffee",
        type: "hashtag",
        thumbnail: hashtagThumbnail("morningcoffee"),
        count: 7_300_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-emma-hash-10",
        name: "dayinmylife",
        type: "hashtag",
        thumbnail: hashtagThumbnail("dayinmylife"),
        count: 11_400_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
]

/**
 * @matildadjerf - Fashion/Scandinavian Style
 * Target audience: Fashion enthusiasts, Scandinavian style lovers, sustainable fashion
 */
const matildaDjerfTargets: DemoTarget[] = [
    // Similar User Accounts - Fashion/Scandinavian Influencers
    {
        id: "demo-matilda-user-1",
        name: "pernilleteisbaek",
        type: "user",
        thumbnail: userThumbnail("pernilleteisbaek"),
        count: 1_800_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-user-2",
        name: "jfriedler",
        type: "user",
        thumbnail: userThumbnail("jfriedler"),
        count: 420_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-user-3",
        name: "emilisindlev",
        type: "user",
        thumbnail: userThumbnail("emilisindlev"),
        count: 980_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-user-4",
        name: "jeannedamas",
        type: "user",
        thumbnail: userThumbnail("jeannedamas"),
        count: 2_100_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-user-5",
        name: "monfreid",
        type: "user",
        thumbnail: userThumbnail("monfreid"),
        count: 560_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-user-6",
        name: "leoniehanne",
        type: "user",
        thumbnail: userThumbnail("leoniehanne"),
        count: 4_900_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-user-7",
        name: "hoskelsa",
        type: "user",
        thumbnail: userThumbnail("hoskelsa"),
        count: 8_900_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-user-8",
        name: "anninamislin",
        type: "user",
        thumbnail: userThumbnail("anninamislin"),
        count: 320_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    // Hashtags - Fashion/Scandinavian Style
    {
        id: "demo-matilda-hash-1",
        name: "scandinavianstyle",
        type: "hashtag",
        thumbnail: hashtagThumbnail("scandinavianstyle"),
        count: 4_800_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-hash-2",
        name: "fashioninfluencer",
        type: "hashtag",
        thumbnail: hashtagThumbnail("fashioninfluencer"),
        count: 32_000_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-hash-3",
        name: "minimalstyle",
        type: "hashtag",
        thumbnail: hashtagThumbnail("minimalstyle"),
        count: 8_200_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-hash-4",
        name: "outfitinspo",
        type: "hashtag",
        thumbnail: hashtagThumbnail("outfitinspo"),
        count: 28_500_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-hash-5",
        name: "streetstyle",
        type: "hashtag",
        thumbnail: hashtagThumbnail("streetstyle"),
        count: 54_000_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-hash-6",
        name: "sustainablefashion",
        type: "hashtag",
        thumbnail: hashtagThumbnail("sustainablefashion"),
        count: 12_600_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-hash-7",
        name: "copenhagenfashion",
        type: "hashtag",
        thumbnail: hashtagThumbnail("copenhagenfashion"),
        count: 1_200_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-hash-8",
        name: "slowfashion",
        type: "hashtag",
        thumbnail: hashtagThumbnail("slowfashion"),
        count: 6_400_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-hash-9",
        name: "neutralstyle",
        type: "hashtag",
        thumbnail: hashtagThumbnail("neutralstyle"),
        count: 3_100_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
    {
        id: "demo-matilda-hash-10",
        name: "everydayfashion",
        type: "hashtag",
        thumbnail: hashtagThumbnail("everydayfashion"),
        count: 5_800_000,
        platform: "instagram",
        source: "ai_onboarding",
    },
]

/**
 * Main export: Map of account ID to targeting data
 */
export const DEMO_TARGETS: Record<string, DemoTarget[]> = {
    [RHODE_ID]: rhodeTargets,
    [EMMA_CHAMBERLAIN_ID]: emmaChamberlainTargets,
    [MATILDA_DJERF_ID]: matildaDjerfTargets,
}

/**
 * Targeting options per demo account
 */
export const DEMO_TARGETING_OPTIONS: Record<string, DemoTargetingOptions> = {
    [RHODE_ID]: {
        languages: ["english"],
        locations: ["united states", "united kingdom", "canada", "australia"],
        interests: ["beauty", "fashion", "health & wellness"],
        general: {
            speed: "normal",
            gender: "female",
        },
    },
    [EMMA_CHAMBERLAIN_ID]: {
        languages: ["english"],
        locations: ["united states", "canada", "united kingdom"],
        interests: ["food", "fashion", "memes/humor", "photography"],
        general: {
            speed: "turbo",
            gender: "any",
        },
    },
    [MATILDA_DJERF_ID]: {
        languages: ["english"],
        locations: ["sweden", "norway", "netherlands", "united states", "united kingdom"],
        interests: ["fashion", "beauty", "travel", "home decor"],
        general: {
            speed: "normal",
            gender: "female",
        },
    },
}

/**
 * Helper to check if an account ID is a demo account
 */
export const isDemoAccount = (accountId: string): boolean => {
    return accountId in DEMO_TARGETS
}

/**
 * Get demo targets for a specific account
 */
export const getDemoTargets = (accountId: string): DemoTarget[] | null => {
    return DEMO_TARGETS[accountId] ?? null
}

/**
 * Get demo targeting options for a specific account
 */
export const getDemoTargetingOptions = (accountId: string): DemoTargetingOptions | null => {
    return DEMO_TARGETING_OPTIONS[accountId] ?? null
}
